/*
 * textlayer.js — reading the text that is ALREADY in the PDF, so it can be
 * edited rather than merely covered.
 *
 * WHAT "EDITING EXISTING TEXT" ACTUALLY IS
 * ────────────────────────────────────────
 * A PDF does not store paragraphs. It stores glyphs placed at coordinates,
 * drawn with a font that is usually an embedded subset containing only the
 * characters that document happens to use. There is no reflow, no line model,
 * and frequently no way to type a character the embedded font does not carry.
 * That is why PDF24 tells users to convert to Word instead, and why every
 * online editor that claims "edit text" — Sejda included — actually does what
 * this module does: cover the old glyphs and draw new ones on top.
 *
 * The difference between doing that well and doing it badly is entirely in the
 * measurements:
 *   - the patch must match the paper, or the edit shows as a white scar;
 *   - the new text must match the old size and colour, or the line jumps;
 *   - the replacement face must actually be able to render what gets typed.
 *
 * So this module measures instead of guessing: geometry from the text item's
 * own transform, colours sampled from the rendered page, and the face chosen
 * by family AND glyph coverage.
 *
 * The pure parts live here with no DOM so tests/js/pdf-editor-textlayer.test.mjs
 * can lock them; the canvas sampling takes raw pixel data for the same reason.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfTextLayer = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── line grouping ──────────────────────────────────────────────────── */

    /*
     * buildLines(items, styles) -> [{ text, items[], x, baseline, width, size,
     *                                 ascent, descent, fontFamily }]
     *
     * pdf.js emits a text item per drawing operation, so one visual line can
     * arrive as several items (a bold run, a number, a font switch) plus empty
     * `hasEOL` markers. Editing one fragment of a sentence is not what a user
     * means by "edit this line", so items sharing a baseline are merged.
     *
     * `transform` is [a, b, c, d, e, f]: `d` is the effective font size and
     * (e, f) is the baseline origin in PDF user space (bottom-left, y up).
     */
    function buildLines(items, styles, opts) {
        opts = opts || {};
        var tolerance = opts.baselineTolerance === undefined ? 2 : opts.baselineTolerance;
        var gapFactor = opts.gapFactor === undefined ? 1.6 : opts.gapFactor;
        var lines = [];

        (items || []).forEach(function (it) {
            if (!it || !it.str || !it.str.trim()) return;      // EOL markers carry no glyphs
            var tr = it.transform || [1, 0, 0, 1, 0, 0];
            var size = Math.abs(tr[3]) || Math.abs(it.height) || 12;
            // A rotated or skewed run (b or c non-zero) is not something we can
            // safely re-typeset upright; leave it to the caller to skip.
            var skewed = Math.abs(tr[1]) > 0.01 || Math.abs(tr[2]) > 0.01;
            var style = (styles && styles[it.fontName]) || {};

            var rec = {
                str: it.str,
                x: tr[4],
                baseline: tr[5],
                width: it.width || 0,
                size: size,
                ascent: style.ascent === undefined ? 0.9 : style.ascent,
                descent: style.descent === undefined ? -0.25 : style.descent,
                fontFamily: style.fontFamily || 'sans-serif',
                fontName: it.fontName,
                skewed: skewed,
            };

            var host = null;
            for (var i = 0; i < lines.length; i++) {
                var L = lines[i];
                if (L.skewed !== rec.skewed) continue;
                if (Math.abs(L.baseline - rec.baseline) > tolerance) continue;
                // Same baseline, but a wide horizontal gap means a separate
                // column, not the same sentence — merging those would let an
                // edit to the left column silently swallow the right one.
                var gap = rec.x - (L.x + L.width);
                if (gap > L.size * gapFactor || rec.x + rec.width < L.x - L.size * gapFactor) continue;
                host = L;
                break;
            }

            if (!host) {
                rec.items = [it];
                rec.text = it.str;
                lines.push(rec);
                return;
            }
            // extend the host line
            var right = Math.max(host.x + host.width, rec.x + rec.width);
            var left = Math.min(host.x, rec.x);
            host.text += (rec.x > host.x + host.width + rec.size * 0.15 ? ' ' : '') + rec.str;
            host.x = left;
            host.width = right - left;
            host.size = Math.max(host.size, rec.size);
            host.ascent = Math.max(host.ascent, rec.ascent);
            host.descent = Math.min(host.descent, rec.descent);
            host.items.push(it);
        });

        return lines.sort(function (a, b) { return b.baseline - a.baseline || a.x - b.x; });
    }

    /*
     * lineBox(line) -> { x, y, w, h }  in PDF user space, bottom-left origin.
     * The visual box, not the baseline: ascent above, descent below, plus a
     * hair of padding so the patch covers antialiased edges instead of leaving
     * a grey ghost of the old glyphs.
     */
    function lineBox(line, opts) {
        opts = opts || {};
        var pad = opts.pad === undefined ? 0.08 : opts.pad;   // fraction of size
        var top = line.baseline + line.ascent * line.size + line.size * pad;
        var bottom = line.baseline + line.descent * line.size - line.size * pad;
        return {
            x: line.x - line.size * pad,
            y: bottom,
            w: line.width + line.size * pad * 2,
            h: top - bottom,
        };
    }

    /*
     * toPlacement(box, page) -> placement in DISPLAY fractions (top-left origin)
     *
     * `page` is {width, height, rotate} where width/height are the UNROTATED
     * MediaBox and rotate is the effective rotation the viewer applies. The
     * result is in the same coordinate system the session model uses for every
     * other object, so an edited line behaves exactly like a placed one.
     */
    function toPlacement(box, page) {
        var rot = ((page.rotate % 360) + 360) % 360;
        var W = page.width, H = page.height;

        // Corners in PDF space → display space, then take the bounding box.
        var pts = [
            [box.x, box.y], [box.x + box.w, box.y],
            [box.x, box.y + box.h], [box.x + box.w, box.y + box.h],
        ].map(function (p) { return pdfToDisplay(p[0], p[1], W, H, rot); });

        var xs = pts.map(function (p) { return p[0]; });
        var ys = pts.map(function (p) { return p[1]; });
        var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
        var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
        var dispW = (rot === 90 || rot === 270) ? H : W;
        var dispH = (rot === 90 || rot === 270) ? W : H;

        return {
            nx: minX / dispW,
            ny: minY / dispH,
            nw: (maxX - minX) / dispW,
            nh: (maxY - minY) / dispH,
            rot: 0,
        };
    }

    /*
     * PDF user space (bottom-left, y up) → display space (top-left, y down),
     * with the page's rotation applied.
     *
     * Derivation, because guessing here is how 90 and 270 end up swapped — as
     * they were in the first cut of this file. Unrotated display is
     * (x, H − y). Rotating that image 90° clockwise maps a display point
     * (dx, dy) to (H − dy, dx), so:
     *     90°  → (H − (H − y), x)       = (y, x)
     *     180° → (W − x, H − (H − y))   = (W − x, y)
     *     270° → (H − y, W − x)
     *
     * Cross-check against the proven inverse in public/js/pdf-coords.js:
     * `mediaToRenderedBL` sends media (x, y) to rendered-BL (y, mediaW − x)
     * for /Rotate 90; flipping that to a top-left origin (viewH = mediaW)
     * gives (y, x). Same answer.
     *
     * A swap here is invisible in a range check — both variants keep points on
     * the page — so the test asserts the QUADRANT, not just that something
     * moved.
     */
    function pdfToDisplay(x, y, W, H, rot) {
        if (rot === 90)  return [y, x];
        if (rot === 180) return [W - x, y];
        if (rot === 270) return [H - y, W - x];
        return [x, H - y];
    }

    /* ── colour sampling ────────────────────────────────────────────────── */

    /*
     * sampleColors({data, width, height}, box) -> { bg, fg, confident }
     *
     * `box` is in the SAME pixel space as the image data.
     *
     * bg: the most common colour in a thin band just outside the text box —
     *     outside means no glyph pixels, so it is the paper, whatever colour
     *     the paper actually is (scans are rarely #ffffff, and patching a
     *     yellowed scan with pure white leaves an obvious rectangle).
     * fg: the darkest colour INSIDE the box — the ink.
     *
     * `confident` is false when the band is not uniform (the line sits on a
     * gradient, a table rule or an image). The caller should then leave the
     * original visible and say so rather than stamping a wrong-coloured patch
     * over someone's document.
     */
    function sampleColors(image, box) {
        var W = image.width, H = image.height, d = image.data;
        var band = Math.max(2, Math.round(box.h * 0.35));

        // Quantise to find the DOMINANT colour, but accumulate the real channel
        // values so the answer is the actual paper, not a bucket midpoint.
        // Returning the midpoint made white paper (255) come back as 240 — a
        // visibly grey patch on every clean document, which is precisely the
        // scar this function exists to avoid. Measured 2026-08-10.
        var counts = {}, sums = {}, total = 0;
        function tally(px, py) {
            if (px < 0 || py < 0 || px >= W || py >= H) return;
            var i = (py * W + px) * 4;
            var key = ((d[i] >> 5) << 6) | ((d[i + 1] >> 5) << 3) | (d[i + 2] >> 5);
            counts[key] = (counts[key] || 0) + 1;
            var s = sums[key] || (sums[key] = [0, 0, 0]);
            s[0] += d[i]; s[1] += d[i + 1]; s[2] += d[i + 2];
            total++;
        }

        var x0 = Math.round(box.x), x1 = Math.round(box.x + box.w);
        var y0 = Math.round(box.y), y1 = Math.round(box.y + box.h);
        for (var x = x0; x < x1; x++) {
            for (var b = 1; b <= band; b++) { tally(x, y0 - b); tally(x, y1 + b); }
        }

        var bestKey = null, bestN = 0;
        Object.keys(counts).forEach(function (k) {
            if (counts[k] > bestN) { bestN = counts[k]; bestKey = k; }
        });
        var bg = { r: 255, g: 255, b: 255 };
        if (bestKey !== null && bestN > 0) {
            var sum = sums[bestKey];
            bg = { r: Math.round(sum[0] / bestN), g: Math.round(sum[1] / bestN), b: Math.round(sum[2] / bestN) };
        }

        // darkest ink inside the box
        var fg = { r: 0, g: 0, b: 0 }, darkest = Infinity, inkPixels = 0;
        for (var yy = y0; yy < y1; yy++) {
            if (yy < 0 || yy >= H) continue;
            for (var xx = x0; xx < x1; xx++) {
                if (xx < 0 || xx >= W) continue;
                var i2 = (yy * W + xx) * 4;
                var lum = 0.299 * d[i2] + 0.587 * d[i2 + 1] + 0.114 * d[i2 + 2];
                if (lum < darkest) { darkest = lum; fg = { r: d[i2], g: d[i2 + 1], b: d[i2 + 2] }; }
                if (lum < 160) inkPixels++;
            }
        }

        return {
            bg: bg,
            fg: fg,
            // The band is "paper" only if one colour dominates it. Below ~70%
            // the line is probably over a rule, a gradient or an image.
            confident: total > 0 && (bestN / total) >= 0.7 && inkPixels > 0,
            uniformity: total ? bestN / total : 0,
        };
    }

    function toHex(c) {
        function h(v) { var s = Math.max(0, Math.min(255, Math.round(v))).toString(16); return s.length < 2 ? '0' + s : s; }
        return '#' + h(c.r) + h(c.g) + h(c.b);
    }

    /* ── face choice ────────────────────────────────────────────────────── */

    /*
     * pickFace(line, text, Fonts) -> faceId
     *
     * Two constraints, and coverage wins: a serif replacement that cannot
     * render the user's alphabet is worse than a sans one that can. Falls back
     * to whatever covers the text when the family preference is unavailable.
     */
    function pickFace(line, text, Fonts) {
        if (!Fonts) return null;
        var family = String(line.fontFamily || '').toLowerCase();
        var preference = /serif/.test(family) && !/sans/.test(family)
            ? ['playfair', 'yeseva', 'noto']
            : /mono/.test(family)
                ? ['noto']
                : ['noto', 'montserrat'];

        for (var i = 0; i < preference.length; i++) {
            /* `faceMeta`: `face` hands back a Promise, which is ALWAYS truthy —
               the «does this face exist?» half of the guard has never rejected
               anything. canRender still gated, so nothing broke visibly. */
            if (Fonts.faceMeta(preference[i]) && Fonts.canRender(text, preference[i])) return preference[i];
        }
        return Fonts.pick(text, null);
    }

    /* ── find & replace ─────────────────────────────────────────────────── */

    /*
     * findMatches(pages, query, opts) -> [{ pageId, line, count }]
     *
     * `pages` is [{ pageId, lines }]. Matching is done on the LINE, because the
     * replacement mechanism is cover-and-retype and the unit it can rewrite is
     * a line. A match spanning two lines cannot be rewritten without reflowing
     * the paragraph, which a PDF has no notion of — so those are reported as
     * `spans: true` and skipped rather than half-replaced.
     */
    function findMatches(pages, query, opts) {
        opts = opts || {};
        var q = String(query || '');
        if (!q) return [];
        var out = [];
        (pages || []).forEach(function (p) {
            (p.lines || []).forEach(function (line) {
                var n = countOccurrences(line.text, q, opts);
                if (n > 0) out.push({ pageId: p.pageId, line: line, count: n });
            });
        });
        return out;
    }

    function normalise(s, opts) {
        var v = String(s);
        if (!opts.matchCase) v = v.toLowerCase();
        // A PDF line often carries non-breaking and thin spaces where the user
        // typed an ordinary one; matching literally would find nothing and the
        // tool would look broken on exactly the documents people paste from.
        return v.replace(/[      ]/g, ' ');
    }

    function countOccurrences(haystack, needle, opts) {
        opts = opts || {};
        var h = normalise(haystack, opts), n = normalise(needle, opts);
        if (!n) return 0;
        var count = 0, i = 0;
        while ((i = h.indexOf(n, i)) !== -1) { count++; i += n.length; }
        return count;
    }

    /*
     * replaceInText(text, find, replace, opts) -> string
     * Whole-word mode exists because "1" inside "2015" is the classic way a
     * replace-all quietly corrupts a document.
     */
    function replaceInText(text, find, replace, opts) {
        opts = opts || {};
        var src = String(text);
        var h = normalise(src, opts), n = normalise(find, opts);
        if (!n) return src;

        var out = '', i = 0;
        while (true) {
            var at = h.indexOf(n, i);
            if (at === -1) { out += src.slice(i); break; }
            if (opts.wholeWord && !isWordBoundary(h, at, n.length)) {
                out += src.slice(i, at + n.length);
                i = at + n.length;
                continue;
            }
            out += src.slice(i, at) + String(replace);
            i = at + n.length;
        }
        return out;
    }

    function isWordBoundary(h, at, len) {
        var before = at === 0 ? '' : h.charAt(at - 1);
        var after = h.charAt(at + len);
        var word = /[0-9A-Za-zЀ-ӿ]/;
        return !word.test(before) && !word.test(after);
    }

    /* ── hit testing ────────────────────────────────────────────────────── */

    /* The line under a click, expressed in display fractions. Picks the
       smallest matching box so overlapping runs resolve to the tightest one. */
    function lineAt(lines, page, at) {
        var best = null, bestArea = Infinity;
        for (var i = 0; i < lines.length; i++) {
            var p = toPlacement(lineBox(lines[i]), page);
            if (at.nx < p.nx || at.nx > p.nx + p.nw) continue;
            if (at.ny < p.ny || at.ny > p.ny + p.nh) continue;
            var area = p.nw * p.nh;
            if (area < bestArea) { bestArea = area; best = { line: lines[i], placement: p }; }
        }
        return best;
    }

    return {
        buildLines: buildLines,
        lineBox: lineBox,
        toPlacement: toPlacement,
        pdfToDisplay: pdfToDisplay,
        sampleColors: sampleColors,
        toHex: toHex,
        pickFace: pickFace,
        lineAt: lineAt,
        findMatches: findMatches,
        countOccurrences: countOccurrences,
        replaceInText: replaceInText,
    };
});
